import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Share2, Check } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import logger from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Weekly "Calm Moments" share strip on the dashboard — compact single-line
// summary with native share sheet fallback.
export const CalmMomentsCard = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/calm-moments`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (err) {
        logger.error("Failed to load calm moments:", err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const buildShareText = () => {
    if (!data) return "";
    const count = data.total_moments;
    const days = data.days_active;
    if (count === 0) return "";
    const header = count === 1
      ? "I had a calm moment this week with Bilateral Calm."
      : `I had ${count} calm moments this week with Bilateral Calm.`;
    const tail = days > 1 ? ` Spread across ${days} days.` : "";
    return `${header}${tail}`;
  };

  const handleShare = async () => {
    const text = buildShareText();
    if (!text) return;
    // Prefer the native share sheet if available (mobile + iOS/Android)
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "Calm moments this week", text });
        return;
      } catch {
        // user cancelled or unsupported — fall back to copy
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied — share it with someone who's rooting for you.");
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      logger.error("Failed to copy share text:", err);
      toast.error("Couldn't copy. Try again in a moment.");
    }
  };

  if (!data) return null;

  const count = data.total_moments;
  const days = data.days_active;
  const empty = count === 0;

  return (
    <section
      className="relative mb-6 overflow-hidden rounded-xl bg-gradient-to-br from-[#F4F7F6] via-white to-[#EAF0F9] border border-slate-100"
      data-testid="calm-moments-card"
    >
      <div className="relative px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/70 border border-slate-100 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-[#3090A8]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5">
            <span
              className="font-outfit font-semibold text-xl text-[#1B2349] leading-none tabular-nums"
              data-testid="calm-moments-count"
            >
              {count}
            </span>
            <span className="text-[11px] uppercase tracking-wider text-[#3090A8] font-medium">
              calm moment{count === 1 ? "" : "s"} this week
            </span>
          </div>
          <p className="text-xs text-[#718096] mt-0.5 truncate">
            {empty
              ? "Complete a session to log your first moment."
              : days >= 3
                ? `Across ${days} days — a rhythm is forming.`
                : days === 1
                  ? "All today — a start."
                  : `Across ${days} days.`}
          </p>
        </div>
        {!empty && (
          <Button
            onClick={handleShare}
            variant="outline"
            size="sm"
            className="border-[#3090A8] text-[#3090A8] hover:bg-[#3090A8] hover:text-white rounded-full flex-shrink-0 h-8 px-3 text-xs"
            data-testid="calm-moments-share-btn"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
          </Button>
        )}
      </div>
    </section>
  );
};

export default CalmMomentsCard;
